import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import MDInput from '../../components/MDInput';
import MDButton from '../../components/MDButton';
import CarbonCalculator from '../../utils/calculateCarbon';

/**
 * Karbon ayak izi hesaplama formu
 * Sektör seçimine göre dinamik olarak alanlar oluşturur
 */
const CarbonFootprintForm = ({ onCalculationComplete, initialData = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedSector, setSelectedSector] = useState(initialData?.sector || '');
  const [sectorFields, setSectorFields] = useState(null);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [calculator] = useState(new CarbonCalculator());
  
  // Otel özel alanları
  const [organizationName, setOrganizationName] = useState(initialData?.organizationName || '');
  const [reportingPeriod, setReportingPeriod] = useState(initialData?.reportingPeriod || 'monthly');
  const [periodStartDate, setPeriodStartDate] = useState(initialData?.periodStartDate || new Date());
  const [periodEndDate, setPeriodEndDate] = useState(initialData?.periodEndDate || new Date());

  // Form validation schema - dinamik olarak oluşturulur
  const createValidationSchema = (fields, sector) => {
    const schemaFields = {};
    
    if (fields) {
      // Sektör metrikleri için validation
      fields.sector_metrics.forEach(metric => {
        if (metric.validation?.required) {
          schemaFields[metric.field] = yup
            .number()
            .required(`${metric.label} zorunludur`)
            .min(metric.validation.min || 0, `En az ${metric.validation.min || 0} olmalıdır`);
        } else {
          schemaFields[metric.field] = yup.number().min(0, 'Negatif değer giremezsiniz');
        }
      });

      // Otel sektörü özel alanları
      if (sector === 'hotel') {
        schemaFields.room_count = yup
          .number()
          .min(1, 'En az 1 oda olmalıdır')
          .integer('Oda sayısı tam sayı olmalıdır');
        
        schemaFields.occupancy_rate = yup
          .number()
          .min(0, 'Doluluk oranı 0\'dan küçük olamaz')
          .max(100, 'Doluluk oranı 100\'den büyük olamaz');
        
        schemaFields.restaurant_covers = yup
          .number()
          .min(0, 'Negatif değer giremezsiniz')
          .integer('Porsiyon sayısı tam sayı olmalıdır');
        
        schemaFields.spa_area = yup
          .number()
          .min(0, 'Negatif alan giremezsiniz');
        
        schemaFields.pool_area = yup
          .number()
          .min(0, 'Negatif alan giremezsiniz');
        
        schemaFields.conference_area = yup
          .number()
          .min(0, 'Negatif alan giremezsiniz');
      }

      // Gerekli aktiviteler için validation
      fields.required_fields.forEach(field => {
        if (field.validation?.required) {
          schemaFields[field.activity] = yup
            .number()
            .required(`${field.label} zorunludur`)
            .min(field.validation.min || 0, `En az ${field.validation.min || 0} olmalıdır`);
        }
      });

      // Opsiyonel aktiviteler için validation
      fields.optional_fields.forEach(field => {
        schemaFields[field.activity] = yup
          .number()
          .min(0, 'Negatif değer giremezsiniz')
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          });
      });
    }

    return yup.object().shape(schemaFields);
  };

  const { control, handleSubmit, reset, formState: { errors }, watch } = useForm({
    resolver: sectorFields ? yupResolver(createValidationSchema(sectorFields, selectedSector)) : undefined,
    defaultValues: initialData?.formData || {}
  });

  // Sektörleri yükle
  useEffect(() => {
    try {
      const sectors = calculator.getAvailableSectors();
      setAvailableSectors(sectors);
    } catch (error) {
      console.error('Error loading sectors:', error);
    }
  }, [calculator]);

  // Sektör değiştiğinde alanları güncelle
  useEffect(() => {
    if (selectedSector) {
      try {
        const fields = calculator.getSectorFields(selectedSector);
        setSectorFields(fields);
        
        // Form'u reset et
        reset();
      } catch (error) {
        console.error('Error loading sector fields:', error);
        setSectorFields(null);
      }
    } else {
      setSectorFields(null);
    }
  }, [selectedSector, calculator, reset]);

  const handleSectorChange = (event) => {
    const sector = event.target.value;
    setSelectedSector(sector);
  };

  const onSubmit = async (formData) => {
    if (!selectedSector || !sectorFields) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Form datasını aktivite formatına dönüştür
      const activities = {};
      const sectorMetrics = {};

      // Sektör metriklerini ayır
      sectorFields.sector_metrics.forEach(metric => {
        if (formData[metric.field] !== undefined && formData[metric.field] !== '') {
          sectorMetrics[metric.field] = formData[metric.field];
        }
      });

      // Aktiviteleri ayır
      [...sectorFields.required_fields, ...sectorFields.optional_fields].forEach(field => {
        if (formData[field.activity] !== undefined && formData[field.activity] !== '' && formData[field.activity] > 0) {
          activities[field.activity] = {
            amount: formData[field.activity],
            unit: field.unit
          };
        }
      });

      // Karbon ayak izi hesapla
      const results = calculator.calculateCarbonFootprint(activities, selectedSector, sectorMetrics);
      
      // Validasyon yap
      const validation = calculator.validateResults(results);
      
      // Sonuçları parent component'e gönder
      if (onCalculationComplete) {
        onCalculationComplete({
          results,
          validation,
          formData: { 
            sector: selectedSector, 
            organizationName,
            reportingPeriod,
            periodStartDate,
            periodEndDate,
            ...formData 
          },
          activities,
          sectorMetrics,
          organizationInfo: {
            name: organizationName,
            period: reportingPeriod,
            startDate: periodStartDate,
            endDate: periodEndDate
          }
        });
      }
    } catch (error) {
      console.error('Calculation error:', error);
      // Hata handling buraya eklenebilir
    } finally {
      setIsLoading(false);
    }
  };

  const renderFormField = (field, isRequired = false) => {
    const fieldError = errors[field.activity];
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={field.activity}>
        <Controller
          name={field.activity}
          control={control}
          render={({ field: controllerField }) => (
            <MDInput
              {...controllerField}
              label={`${field.label} (${field.unit})`}
              type="number"
              size={isMobile ? "small" : "medium"}
              inputProps={{ 
                min: 0,
                step: field.type === 'number' ? 0.01 : 1
              }}
              error={!!fieldError}
              helperText={fieldError?.message}
              required={isRequired}
              placeholder={`Miktar (${field.unit})`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.9rem', md: '1rem' }
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', md: '1rem' }
                },
                '& .MuiFormHelperText-root': {
                  fontSize: { xs: '0.75rem', md: '0.8rem' }
                }
              }}
            />
          )}
        />
      </Grid>
    );
  };

  const renderSectorMetricField = (metric) => {
    const fieldError = errors[metric.field];
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={metric.field}>
        <Controller
          name={metric.field}
          control={control}
          render={({ field: controllerField }) => (
            <MDInput
              {...controllerField}
              label={`${metric.label} (${metric.unit})`}
              type="number"
              size={isMobile ? "small" : "medium"}
              inputProps={{ 
                min: metric.validation?.min || 0,
                step: metric.type === 'number' ? 0.01 : 1
              }}
              error={!!fieldError}
              helperText={fieldError?.message || `Bu değer sektör yoğunluğu hesaplaması için gereklidir`}
              required={metric.validation?.required}
              placeholder={`Değer (${metric.unit})`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.9rem', md: '1rem' }
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', md: '1rem' }
                },
                '& .MuiFormHelperText-root': {
                  fontSize: { xs: '0.75rem', md: '0.8rem' }
                }
              }}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      py: { xs: 2, md: 4 },
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      <Paper 
        elevation={24} 
        sx={{ 
          p: { xs: 3, sm: 4, md: 5 }, 
          mb: 3,
          borderRadius: { xs: 2, md: 4 },
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: { xs: 'none', md: 'translateY(-2px)' },
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
          }
        }}
      >
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: { xs: 3, sm: 4, md: 5 },
          m: { xs: -3, sm: -4, md: -5 },
          mb: 4,
          borderRadius: { xs: '8px 8px 0 0', md: '16px 16px 0 0' }
        }}>
          <Typography 
            variant={isMobile ? "h4" : "h3"}
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
            }}
          >
            🌱 Karbon Ayak İzi
          </Typography>
          <Typography 
            variant={isMobile ? "subtitle1" : "h5"}
            sx={{ 
              opacity: 0.9,
              fontWeight: 300,
              fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' }
            }}
          >
            Sürdürülebilir Gelecek İçin Hesaplama Sistemi
          </Typography>
        </Box>
      
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Organizasyon Bilgileri */}
          <Paper 
            elevation={8} 
            sx={{ 
              p: { xs: 3, sm: 4 }, 
              mb: 4, 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              borderRadius: { xs: 2, md: 3 },
              boxShadow: '0 10px 30px rgba(240, 147, 251, 0.3)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: { xs: 'none', md: 'translateY(-2px)' },
                boxShadow: '0 15px 40px rgba(240, 147, 251, 0.4)'
              }
            }}
          >
            <Typography 
              variant={isMobile ? "h6" : "h5"}
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 2 },
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              🏢 Organizasyon Bilgileri
            </Typography>
            
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organizasyon/Otel Adı"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  required
                  placeholder="Örn: Grand Hotel Ankara"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: { xs: 1, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'white',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(240, 147, 251, 0.8)',
                      px: 1,
                      borderRadius: 1,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&.Mui-focused': {
                        color: 'white'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', md: '0.8rem' }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      borderWidth: 2
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.8)'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(240, 147, 251, 0.8)',
                    px: 1,
                    borderRadius: 1,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    '&.Mui-focused': {
                      color: 'white'
                    }
                  }}>
                    Raporlama Dönemi
                  </InputLabel>
                  <Select
                    value={reportingPeriod}
                    onChange={(e) => setReportingPeriod(e.target.value)}
                    label="Raporlama Dönemi"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: { xs: 1, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        borderWidth: 2
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.8)'
                      }
                    }}
                  >
                    <MenuItem value="monthly">📅 Aylık</MenuItem>
                    <MenuItem value="quarterly">📊 Çeyreklik</MenuItem>
                    <MenuItem value="yearly">📈 Yıllık</MenuItem>
                    <MenuItem value="custom">🗓️ Özel Dönem</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Dönem Başlangıcı"
                  type="date"
                  value={periodStartDate ? periodStartDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setPeriodStartDate(e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: { xs: 1, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'white',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(240, 147, 251, 0.8)',
                      px: 1,
                      borderRadius: 1,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&.Mui-focused': {
                        color: 'white'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', md: '0.8rem' }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      borderWidth: 2
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.8)'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Dönem Sonu"
                  type="date"
                  value={periodEndDate ? periodEndDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setPeriodEndDate(e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: { xs: 1, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'white',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(240, 147, 251, 0.8)',
                      px: 1,
                      borderRadius: 1,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '&.Mui-focused': {
                        color: 'white'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', md: '0.8rem' }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      borderWidth: 2
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.8)'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

        {/* Sektör Seçimi */}
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            borderRadius: { xs: 2, md: 3 },
            boxShadow: '0 10px 30px rgba(79, 172, 254, 0.3)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: { xs: 'none', md: 'translateY(-2px)' },
              boxShadow: '0 15px 40px rgba(79, 172, 254, 0.4)'
            }
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"}
            gutterBottom 
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              mb: 3,
              textAlign: 'center',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            🎯 Sektör Seçimi
          </Typography>
          <FormControl fullWidth size={isMobile ? "small" : "large"}>
            <InputLabel sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              backgroundColor: 'rgba(79, 172, 254, 0.8)',
              px: 1,
              borderRadius: 1,
              fontSize: { xs: '0.9rem', md: '1rem' },
              '&.Mui-focused': {
                color: 'white'
              }
            }}>
              Sektör Seçiniz
            </InputLabel>
            <Select
              value={selectedSector}
              onChange={handleSectorChange}
              label="Sektör Seçiniz"
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: { xs: 1, md: 2 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                '& .MuiSelect-select': { 
                  py: { xs: 1.5, md: 2 },
                  fontWeight: 'bold',
                  fontSize: { xs: '0.9rem', md: '1rem' }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  borderWidth: 2
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            >
            {availableSectors.map((sector) => (
              <MenuItem key={sector.key} value={sector.key}>
                {sector.name} ({sector.output_metric.label})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        </Paper>        {sectorFields && (
          <>
            {/* Sektör Bilgisi */}
            <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.9rem', md: '1rem' } }}>
              <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                {sectorFields.sector_info.name} sektörü seçildi. 
                Sonuç {sectorFields.sector_info.output_metric.label} cinsinden hesaplanacaktır.
              </Typography>
            </Alert>

            {/* Sektör Metrikleri */}
            {sectorFields.sector_metrics.length > 0 && (
              <>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"}
                  gutterBottom 
                  sx={{ 
                    mt: 3, 
                    mb: 2,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                    fontWeight: 'bold'
                  }}
                >
                  📊 Sektör Metrikleri
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  {sectorFields.sector_metrics.map(renderSectorMetricField)}
                </Grid>
                <Divider sx={{ my: 4 }} />
              </>
            )}

            {/* Otel Özel Alanları */}
            {selectedSector === 'hotel' && (
              <>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"}
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 3,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                    fontWeight: 'bold',
                    flexWrap: 'wrap'
                  }}
                >
                  🏨 Otel Özel Bilgileri
                  <Chip label="Otel Sektörü" size="small" color="info" />
                </Typography>
                
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name="room_count"
                      control={control}
                      render={({ field }) => (
                        <MDInput
                          {...field}
                          label="Toplam Oda Sayısı (adet)"
                          type="number"
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 1, step: 1 }}
                          error={!!errors.room_count}
                          helperText={errors.room_count?.message || "Otelin toplam oda kapasitesi"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name="occupancy_rate"
                      control={control}
                      render={({ field }) => (
                        <MDInput
                          {...field}
                          label="Doluluk Oranı (%)"
                          type="number"
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          error={!!errors.occupancy_rate}
                          helperText={errors.occupancy_rate?.message || "Dönem boyunca ortalama doluluk"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name="restaurant_covers"
                      control={control}
                      render={({ field }) => (
                        <MDInput
                          {...field}
                          label="Restoran Servis Sayısı (porsiyon)"
                          type="number"
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0, step: 1 }}
                          error={!!errors.restaurant_covers}
                          helperText={errors.restaurant_covers?.message || "Dönem boyunca servis edilen yemek sayısı"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name="spa_area"
                      control={control}
                      render={({ field }) => (
                        <MDInput
                          {...field}
                          label="SPA/Wellness Alanı (m²)"
                          type="number"
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0, step: 1 }}
                          error={!!errors.spa_area}
                          helperText={errors.spa_area?.message || "SPA ve wellness tesislerinin alanı"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name="pool_area"
                      control={control}
                      render={({ field }) => (
                        <MDInput
                          {...field}
                          label="Havuz Alanı (m²)"
                          type="number"
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0, step: 1 }}
                          error={!!errors.pool_area}
                          helperText={errors.pool_area?.message || "Toplam havuz alanı"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name="conference_area"
                      control={control}
                      render={({ field }) => (
                        <MDInput
                          {...field}
                          label="Konferans/Toplantı Alanı (m²)"
                          type="number"
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0, step: 1 }}
                          error={!!errors.conference_area}
                          helperText={errors.conference_area?.message || "Toplantı salonları alanı"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                
                <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    💡 <strong>Otel Sektörü İpuçları:</strong><br/>
                    • Misafir-gece sayısı: oda sayısı x doluluk oranı x gün sayısı<br/>
                    • Enerji tüketimi odalara, ortak alanlara ve operasyonel alanlara göre detaylandırılabilir<br/>
                    • SPA, havuz, restoran gibi ek tesisler enerji tüketimini artırır
                  </Typography>
                </Alert>
                
                <Divider sx={{ my: 4 }} />
              </>
            )}

            {/* Gerekli Aktiviteler */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"}
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: 'bold',
                flexWrap: 'wrap'
              }}
            >
              ⚡ Gerekli Aktiviteler 
              <Chip label="Zorunlu" size="small" color="error" />
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
              {sectorFields.required_fields.map(field => renderFormField(field, true))}
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Opsiyonel Aktiviteler */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"}
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: 'bold',
                flexWrap: 'wrap'
              }}
            >
              🔧 Opsiyonel Aktiviteler
              <Chip label="İsteğe Bağlı" size="small" color="default" />
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
              {sectorFields.optional_fields.map(field => renderFormField(field, false))}
            </Grid>

            {/* Submit Button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 4, 
              pt: 3, 
              borderTop: 1, 
              borderColor: 'divider' 
            }}>
              <MDButton
                type="submit"
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                disabled={isLoading}
                sx={{ 
                  minWidth: { xs: '100%', sm: 300 }, 
                  py: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 'bold',
                  borderRadius: { xs: 2, md: 3 }
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Hesaplanıyor...
                  </>
                ) : (
                  <>
                    🧮 Karbon Ayak İzi Hesapla
                  </>
                )}
              </MDButton>
            </Box>
          </>
        )}

        {selectedSector && !sectorFields && (
          <Alert severity="warning" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
              Seçilen sektör için form alanları yüklenemiyor. Lütfen farklı bir sektör seçin.
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
    </Box>
  );
};

export default CarbonFootprintForm;
